import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Bell, Send } from "lucide-react";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NotificationsPage({ user }) {
  const [notifications, setNotifications] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    target_disciples: []
  });

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      toast.error("Failed to fetch notifications");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/notifications`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Notification sent successfully");
      fetchNotifications();
      setDialogOpen(false);
      setFormData({ title: "", message: "", target_disciples: [] });
    } catch (error) {
      toast.error("Failed to send notification");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          <span className="gradient-text">Notifications</span>
        </h1>
        <p className="text-gray-600">Broadcast messages to disciples</p>
      </div>

      <div className="stat-card mb-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/80 text-sm">Total Notifications Sent</p>
            <p className="text-3xl font-bold" data-testid="total-notifications">{notifications.length}</p>
          </div>
          <Bell className="h-12 w-12 text-white/40" />
        </div>
      </div>

      <div className="card mb-6">
        <div className="flex justify-end">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="send-notification-button" className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700">
                <Plus className="mr-2 h-4 w-4" /> Send Notification
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>Send New Notification</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    data-testid="notification-title"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter notification title"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    data-testid="notification-message"
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Enter your message"
                    rows={5}
                  />
                </div>

                <p className="text-sm text-gray-500">
                  This will be broadcasted to all disciples in the system.
                </p>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button data-testid="submit-notification-button" type="submit" disabled={loading} className="bg-gradient-to-r from-purple-600 to-indigo-600">
                    <Send className="mr-2 h-4 w-4" />
                    {loading ? "Sending..." : "Send Notification"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="space-y-4">
        {notifications.map((notification) => (
          <div key={notification.id} className="card" data-testid={`notification-${notification.id}`}>
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center flex-shrink-0">
                <Bell className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-lg">{notification.title}</h3>
                  <span className="text-sm text-gray-500">
                    {format(new Date(notification.created_at), "MMM d, yyyy")}
                  </span>
                </div>
                <p className="text-gray-700 mb-2">{notification.message}</p>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span>Sent by {notification.sender_name}</span>
                  <span>•</span>
                  <span>To all disciples</span>
                </div>
              </div>
            </div>
          </div>
        ))}

        {notifications.length === 0 && (
          <div className="text-center py-12">
            <Bell className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No notifications sent yet. Send your first notification!</p>
          </div>
        )}
      </div>
    </div>
  );
}